import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useMemo, useState, useEffect } from "react";
import ModalLayout from "../../components/Layout/ModalLayout";
import AgencySummaryModal from "../../components/Modal/RegistrationSummaryModal/AgencySummaryModal";
import CountrySummaryModal from "../../components/Modal/RegistrationSummaryModal/CountrySummaryModal";
import PackageSummaryModal from "../../components/Modal/RegistrationSummaryModal/PackageSummaryModal";
import RepresentativeSummeryModal from "../../components/Modal/RegistrationSummaryModal/RepresentativeSummaryModal";
import { commonUserListColumns } from "../../components/Table/CommonColumnsForUserList";
import { calculateUserMedicalStatus } from "../../components/Users/calculateUserMedicalStatus";
import UserListReportSummary from "../../components/Users/UserListReportSummary";
import UserSearchForm from "../../components/Users/UserSearchForm";
import { useFetchPatientsData } from "../../hooks/useFetchPatientData";
import "../../Pages/ProcessAndProcedure/MedicalReports/MedicalReports.scss";
import Loading from "../../utils/Loader/Loading";
import ResultPageMultipleSelectBtns from "./ResultPageMultipleSelectBtns";
import SelectedUserResult from "./SelectedUserResult";
import formatDate from "../../utils/formatDate";

const ResultPage = () => {
  // const [usersData, setUsersData] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [filterData, setFilterData] = useState({
    fromDate: formatDate(new Date(), "yyyy-MM-dd"),
    toDate: formatDate(new Date(), "yyyy-MM-dd"),
  });

  const [loading, setLoading] = useState(false);

  const {
    data: {
      users: usersData,
      countryWiseUser: countryWiseUsers,
      packageWiseUser: packageWiseUsers,
      introducerWiseUser: introducerWiseUsers,
      representativeWiseUser: representativeWiseUsers,
    },
    isFetching,
    isLoading,
    refetch,
  } = useFetchPatientsData(filterData);

  const openModal = (rowData) => {
    setSelectedRowData(rowData);
    setModalOpen(true);
  };

  // Function to close the modal
  const closeModal = () => {
    setSelectedRowData(null);
    setModalOpen(false);
  };

  const columns = useMemo(() => {
    // Move the column filtering logic to a separate function for clarity
    const filterColumns = (columns) =>
      columns.filter(
        (item) =>
          item &&
          item.id !== "status" &&
          item.id !== "amount" &&
          item.id !== "created_by"
      );

    // Apply the filter to the common columns
    return filterColumns(commonUserListColumns(usersData));
  }, [usersData]);

  const table = useReactTable({
    data: usersData,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
  });

  // loading false when fetching is done
  useEffect(() => {
    if (!isFetching) {
      setLoading(false);
    }
  }, [isFetching]);

  return (
    <>
      <div className="card medical-report-container">
        <div className="card-body">
          <div className="rounded">
            <div className="card shadow-none mb-3">
              <div className="border p-3 rounded">
                <h6
                  className="mb-0 text-uppercase fw-bold"
                  style={{ color: "red" }}
                >
                  Results
                </h6>

                <UserSearchForm
                  userSearchHandler={(filterData) => {
                    setFilterData(filterData);
                    setLoading(true);
                    refetch();
                  }}
                  isLoading={loading && isFetching}
                />
              </div>
            </div>

            <ResultPageMultipleSelectBtns table={table} />

            <UserListReportSummary userList={usersData} />

            {isLoading || (loading && isFetching) ? (
              <Loading />
            ) : (
              <table className="table align-middle table-hover table-bordered mb-0 rounded">
                <thead className="bg-primary">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header, i) => (
                        <th
                          key={i}
                          id={`th-${i}`}
                          className="text-light p-1"
                          style={{ backgroundColor: "#095d7e" }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row, i) => {
                    const { isDone, status } = calculateUserMedicalStatus(
                      row?.original
                    );

                    const rowClass = (() => {
                      if (status === 2) {
                        return isDone ? "text-success" : "";
                      } else if (status === 3) {
                        return isDone ? "text-danger" : "";
                      } else if (status === 4) {
                        return isDone ? "text-primary" : "";
                      } else {
                        return "text-dark";
                      }
                    })();
                    return (
                      <tr key={i} className={rowClass}>
                        {row.getVisibleCells().map((cell, cellIndex) => (
                          <td
                            key={cell.id}
                            className="p-1 cursor-pointer"
                            onClick={(event) => {
                              // If the clicked column is 0, do nothing
                              if (cellIndex === 0) {
                                return;
                              }
                              event.stopPropagation(); // Prevent row click event
                              // console.log(`Row: ${i}, Column: ${cellIndex}`);
                              openModal(row.original); // Open modal when clicking on other columns
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <ModalLayout
          isOpen={isModalOpen}
          onClose={closeModal}
          title="User Details"
          size="xl"
        >
          <SelectedUserResult userData={selectedRowData} onClose={closeModal} />
        </ModalLayout>
      )}

      {/* Registration Summery Modal Body */}
      <div>
        <CountrySummaryModal
          countryWiseUsers={countryWiseUsers}
          isColumn2={true}
        />
        <AgencySummaryModal
          introducerWiseUsers={introducerWiseUsers}
          isColumn2={true}
        />
        <PackageSummaryModal packageWiseUsers={packageWiseUsers} isColumn2 />
        <RepresentativeSummeryModal
          representativeWiseUsers={representativeWiseUsers}
          isColumn2={true}
        />
      </div>
    </>
  );
};

export default React.memo(ResultPage);
